import React, {
	useEffect, useState, useRef, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Comlink from 'comlink';
/* eslint-disable import/no-webpack-loader-syntax */
import SearchWorker from 'worker-loader!../../../workers/search-worker';
import { loadInvitations } from '../../../stores/actions/invitations';
import { setSelectedMeeting, setOnboardingComplete } from '../../../stores/actions/session';
import { setShowCalendar, setSelectedCalendarView } from '../../../stores/actions/user-interface';
import { calendarOptions } from '../../../stores/reducers/user-interface';
import { LoadMeetingFromShowroomsID } from '../../../connection/meeting';
import Colors from '../../../utils/colors';
import EventList from './event-list';
import EventCard from '../../general-components/event-card';
import LoadingSpinner from '../../general-components/loading-spinner';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import Switch from '../../general-components/switch';
import CalendarView from '../home/calendar-view';
import SelectSmall from '../../general-components/select-small';
import { APP } from '../../../stores/session-status';
import { GreenroomState } from '../../../stores/types/types';
import useJoin from '../../../hooks/use-join';

let searchWorker: any;

(async function init() {
	const worker = new SearchWorker();
	searchWorker = Comlink.wrap(worker) as any;
}());

interface Props {
	connected: boolean;
	eventsAndShowSessions?: any[];
}

export default function ProducerSelectMeeting(props: Props) {
	const loadingEvents = useSelector((state: GreenroomState) => state.ContentReducer.loadingEvents);
	const liveChannels = useSelector((state: GreenroomState) => state.ContentReducer.liveChannels);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const showroomsTokens = useSelector(
		(state: GreenroomState) => state.SessionReducer.showroomsTokens,
	);
	const appSignedInWith = useSelector(
		(state: GreenroomState) => state.SessionReducer.appSignedInWith,
	);
	const listSortType = useSelector(
		(state: GreenroomState) => state.UserInterfaceReducer.listSortType,
	);
	const showCalendar = useSelector(
		(state: GreenroomState) => state.UserInterfaceReducer.showCalendar,
	);
	const selectedCalendarView = useSelector(
		(state: GreenroomState) => state.UserInterfaceReducer.selectedCalendarView,
	);

	const dispatch = useDispatch();

	const { connected, eventsAndShowSessions } = props;

	const { getCurrentOrNewMeeting, joinGreenroom } = useJoin();

	const [filteredEvents, setFilteredEvents] = useState<any[]>();
	const [searchString, setSearchString] = useState<string>('');
	const [creatingMeeting, setCreatingMeeting] = useState<any>(null);
	const [upcoming, setUpcoming] = useState<any>([]);
	const searchDebounce = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (eventsAndShowSessions) {
			searchWorker?.sort(listSortType, eventsAndShowSessions).then(async () => {
				setFilteredEvents(await searchWorker.results);
				if (!searchString) setSearchString(await searchWorker.raw);
			});
		}
	}, [listSortType, eventsAndShowSessions]);

	function twoUpcomingEvents(meetings: any): any[] {
		const now = new Date();

		const upcomingMeetings = meetings
			.filter((meeting: any) => new Date(meeting.end_time) >= now)
			.sort((a: any, b: any) => (a.start_time > b.start_time ? 1 : -1));

		return upcomingMeetings.length > 1
			? [upcomingMeetings[0], upcomingMeetings[1]]
			: upcomingMeetings;
	}

	useEffect(() => {
		if (eventsAndShowSessions) {
			setUpcoming(twoUpcomingEvents(eventsAndShowSessions));
		}
	}, [eventsAndShowSessions]);

	function filterEvents(e: React.KeyboardEvent<HTMLInputElement>) {
		if (!eventsAndShowSessions) {
			return;
		}

		dispatch(setShowCalendar(false));

		const target = e.target as HTMLInputElement;
		const val: string = target.value;

		if (searchDebounce.current) {
			clearTimeout(searchDebounce.current);
		}

		searchDebounce.current = setTimeout(() => {
			searchWorker.search(val, eventsAndShowSessions, listSortType).then(async () => {
				setFilteredEvents(await searchWorker.results);
			});
		}, 250);
	}

	// A row can represent a BL event, Showrooms/AllHands session, or a Standalone
	// GR meeting. This row may or may not have a GR meeting yet.
	async function selectMeeting(row: any) {
		// No GR meeting exists yet for this event/showroom. Need to create GR meeting and then load.
		if (!row.meeting) setCreatingMeeting(row);

		let id = '';
		let showroomsToken = '';

		switch (appSignedInWith) {
			case APP.EVENTS:
				id = row.events_session_uuid;
				break;
			case APP.EVENTS_LEGACY:
				id = row.live_chat;
				break;
			// TO DO Showrooms and Allhands
			case APP.SHOWROOMS:
			case APP.ALL_HANDS:
				id = row.id;
				showroomsToken = showroomsTokens?.accessToken!;
				break;
			default:
				// do nothing
		}

		/*
		 * For all of our apps except Standalone GR when a producer clicks to join they are
		 * either creating a new meeting that doesn't exist or updating an existing meeting
		 * (created from an Event, Showrooms session, etc) to keep necessary fields up to
		 * date for things like rehearsal emails, etc. which are read from the gr_meetings
		 * table. Standalone GRs don't create or update a meeting here because each row is
		 * the actual meeting already.
		 */
		let result = row;

		if (token && row && appSignedInWith !== APP.GREENROOM) {
			result = await getCurrentOrNewMeeting(
				token,
				row,
				appSignedInWith,
				{
					showroomsToken,
				},
				{
					selectedApp: appSignedInWith,
				},
			);
		}

		joinGreenroom(result);
	}

	const createCards = useMemo(() => upcoming.map(
		(event: any, index: number): JSX.Element | undefined => (
			<EventCard
				key={index}
				connected={connected}
				event={event}
				featured={true}
				joinGreenroom={selectMeeting}
				buttonDisabled={event === creatingMeeting}
				showSessionType={event.show_session_type}
			/>
		),
	), [upcoming, liveChannels, connected, creatingMeeting]);

	useEffect(() => {
		// if they start searching, show list view instead of calendar
		if (searchString && !showCalendar) {
			dispatch(setShowCalendar(false));
		}
	}, [searchString]);

	return (
		<div className="select-content">
			<div className="select-content-container">
				{/* eslint-disable-next-line no-nested-ternary */}
				{loadingEvents || !filteredEvents
					? (
						<div className="loading-container">
							<LoadingSpinner
								height={100}
								width={100}
								color={Colors.red}
							/>
						</div>
					) : (eventsAndShowSessions && eventsAndShowSessions.length > 0
						? (
							<>
								{upcoming.length > 0
									&& <div className="featured-cards-container">
										<div className="title-container">
											<h1>Up Next</h1>
										</div>
										<div className="featured-cards">
											{createCards}
										</div>
									</div>
								}
								<div className="select-content-header">
									<div className="all-shows">
										<h1>All Shows</h1>
									</div>

									<div className="select-content-controls">

										{showCalendar && <SelectSmall
											options={calendarOptions}
											onChange={(selectedView) => dispatch(setSelectedCalendarView(selectedView))}
											label={
												calendarOptions.find((t) => t.value === selectedCalendarView)?.label
												?? calendarOptions[0].label}
											selected={
												calendarOptions.find((t) => t.value === selectedCalendarView)
												?? calendarOptions[0]}
											dropdown={true}
										/>}
										<div className="search-field">
											<input type="text" onKeyUp={filterEvents} placeholder={'Search'} onChange={(e) => setSearchString(e.target.value)} value={searchString} />
											<span className="search-icon">
												<Icon name={ICONS.SEARCH} color={COLORS.DEFAULT_GRAY} size={14}></Icon>
											</span>
										</div>
										<div className="calendar-switch-container">
											<Switch
												on={!showCalendar}
												onChange={() => dispatch(setShowCalendar(!showCalendar))}
											/>
											<span className="calendar-icon">
												<Icon
													name={ICONS.CALENDAR}
													color={showCalendar ? COLORS.WHITE : COLORS.GRAY}
													size={14}
												/>
											</span>
											<span className="list-icon">
												<Icon
													name={ICONS.LIST}
													color={!showCalendar ? COLORS.WHITE : COLORS.GRAY}
													size={14}
												/>
											</span>
										</div>
									</div>
								</div>
								{!showCalendar
									? (
										<EventList
											searchString={searchString}
											filteredEvents={filteredEvents}
											connected={connected}
											join={selectMeeting}
											forPresenter={false}
											creatingMeeting={creatingMeeting}
										/>
									) : (
										<CalendarView
											showSessions={eventsAndShowSessions}
											calendarType={selectedCalendarView}
										/>
									)}
							</>
						) : (
							<div className="empty">
								<p>There are no broadcasts scheduled yet</p>
							</div>
						)
					)
				}
			</div>
		</div >
	);
}
